import React, { FunctionComponent, useEffect, useState, useRef } from 'react'
import ReactQuill from 'react-quill-new'
import MultiSelectComponent from 'Features/MultiSelectComponent/MultiSelectComponent'
import { IForumTag, IForumCreateNewThreadResponse, ISelectOption } from 'Frontend/scripts/models/forums.model'
import { getForumTags, createForumThread } from 'Frontend/scripts/services/forums.service'
import { Button, Form } from 'react-bootstrap'
import { quillModules, getSemanticHtml } from 'Frontend/scripts/quillModules'
import { IAntiforgeryToken } from 'Frontend/scripts/models/general.model'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface CreateThreadForm {
  heading: string
  mainbody: string
  tags: ISelectOption[]
}

interface CreateNewThreadProps {
  forumListingPageUrl: string
}

const CreateNewThread: FunctionComponent<CreateNewThreadProps> = ({ forumListingPageUrl }) => {
  const [tags, setTags] = useState<ISelectOption[]>([])
  const [validated, setValidated] = useState(false)
  const options = [] as ISelectOption[]
  const quillRef = useRef<ReactQuill | null>(null)
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateThreadForm>({
    defaultValues: {
      heading: '',
      mainbody: '',
      tags: [],
    },
  })
  const { t } = useTranslation()

  useEffect(() => {
    getForumTags().then((data: IForumTag[]) => {
      options.length = 0
      data.map((tag) => {
        options.push({ value: tag.id.toString(), label: tag.name })
      })
      setTags(options)
    })
  }, [])

  const submitNewThread = (data: CreateThreadForm) => {
    const selectedTagsValueList = data.tags?.map((item: ISelectOption) => item.value.toString())
    const mainBodyWithCorrectHTML = getSemanticHtml(quillRef)
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      createForumThread(token.csrfToken, data.heading, mainBodyWithCorrectHTML, selectedTagsValueList).then(
        (response: IForumCreateNewThreadResponse) => {
          window.location.href = response.url
        },
      )
    })

    setValidated(true)
  }

  return (
    <>
      <Form
        noValidate
        validated={validated}
        onSubmit={handleSubmit(submitNewThread)}
        className="d-flex flex-column gap-3"
      >
        <Form.Group>
          <Form.Label htmlFor="inputThreadHeading">{t('heading')}</Form.Label>
          <Form.Control
            required
            type="text"
            id="inputThreadHeading"
            {...register('heading', {
              required: t('error-add-heading'),
              maxLength: {
                value: 255,
                message: t('error-text-to-long'),
              },
            })}
          />
          {errors.heading && (
            <Form.Text className="text-danger invalid-feedback d-block">{errors.heading.message}</Form.Text>
          )}
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="inputThreadMainBody">{t('text')}</Form.Label>
          <Controller
            name="mainbody"
            control={control}
            rules={{
              validate: (value) => value?.replace(/<(.|\n)*?>/g, '').trim().length > 0 || t('error-text'),
            }}
            render={({ field }) => (
              <ReactQuill
                {...field}
                ref={quillRef}
                id="inputThreadMainBody"
                className="notice__quill-editor"
                theme="snow"
                modules={quillModules}
                onChange={(value) => setValue('mainbody', value, { shouldValidate: true })}
              />
            )}
          />
          {errors.mainbody && (
            <Form.Text className="text-danger invalid-feedback d-block">{errors.mainbody.message}</Form.Text>
          )}
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="multiSelectTags">{t('question-is-about')}</Form.Label>
          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <MultiSelectComponent
                id="multiSelectTags"
                options={tags}
                addToUrl={false}
                placeholderText={t('choose-categories')}
                value={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
        </Form.Group>
        <div className="d-flex gap-3">
          <Button variant="secondary" type="submit">
            {t('save-and-publish')}
          </Button>
          {forumListingPageUrl && (
            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => (window.location.href = forumListingPageUrl)}
            >
              {t('abort')}
            </Button>
          )}
        </div>
      </Form>
    </>
  )
}
export default CreateNewThread
